import { motion } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import GlobalHeader from "@/components/GlobalHeader";
import boatsGraphic from "@/assets/yrd-boats-graphic.svg";
import { PHOTOS } from "@/lib/photos";
import { usePageSEO } from "@/hooks/use-page-seo";
import Footer from "@/components/Footer";
import SponsorsBlock from "@/components/SponsorsBlock";
import { saveEmail, n8nSaveEmail, n8nSaveEmailSS, sendMetaLeadEvent } from "@/lib/firebase";
import { validateEmail } from "@/lib/validation";
import { canSubmit, recordSubmission, getSecondsRemaining } from "@/lib/rate-limit";
import { Mail } from "lucide-react";

// 2027 EARLY ACCESS (2026-07-19): the festival is over — /tickets stays the
// permanent CTA URL sitewide, but it now captures the 2027 early-access list.
// When the 2027 on-sale opens, this page flips back to the OnTourHQ checkout
// (see git history at 9dd8209 for the full selling layout).

const perks = [
  {
    num: "01",
    title: "Dates Before Anyone",
    blurb: "The 2027 dates hit your inbox before they're announced anywhere else",
  },
  {
    num: "02",
    title: "Tickets Before the Public",
    blurb: "Early-access members buy first — before the on-sale opens to everyone",
  },
  {
    num: "03",
    title: "Lineup News First",
    blurb: "Every headliner announcement, straight to you before it goes public",
  },
];

// Inline email capture — same pipeline as the pop-up (Firestore + n8n + Meta Lead).
const EarlyAccessForm = ({ inputId }: { inputId: string }) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    if (!canSubmit("email")) {
      setErrorMessage(`Please wait ${getSecondsRemaining("email")}s before submitting again.`);
      return;
    }
    const validationError = validateEmail(email);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await Promise.all([
        saveEmail(email, "early-access"),
        n8nSaveEmail(email, "early-access").catch(console.error),
        n8nSaveEmailSS(email, "early-access").catch(console.error),
        sendMetaLeadEvent({ email }, "early-access").catch(console.error),
      ]);
      localStorage.setItem("yrd_email_submitted", "true");
      localStorage.setItem("yrd_2027_completed", "true");
      recordSubmission("email");
      const w = window as unknown as { fbq?: (...args: unknown[]) => void; gtag?: (...args: unknown[]) => void };
      try { w.fbq?.("track", "Lead", { content_name: "tickets-page-2027" }); } catch { /* noop */ }
      try { w.gtag?.("event", "early_access_signup", { campaign: "early-access-2027", page: "tickets" }); } catch { /* noop */ }
      setDone(true);
    } catch (error) {
      console.error("Error saving email:", error);
      setErrorMessage("Something went wrong — please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-xl mx-auto rounded-2xl bg-white/10 border border-white/20 px-6 py-6 text-center"
      >
        <p className="text-yacht-yellow font-bold text-lg md:text-xl mb-1">You're on the list! 🎉</p>
        <p className="text-white/80 text-sm md:text-base">
          You'll get first access when 2027 goes live — before anyone else hears about it.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-xl mx-auto">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-yacht-charcoal/40" />
          <input
            type="email"
            id={inputId}
            name="email"
            autoComplete="email"
            inputMode="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full pl-12 pr-4 py-4 bg-white rounded-full text-yacht-charcoal placeholder:text-yacht-charcoal/40 focus:outline-none focus:ring-2 focus:ring-yacht-yellow text-base md:text-lg"
          />
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-8 py-4 rounded-full bg-yacht-yellow text-yacht-charcoal font-bold text-base md:text-lg hover:brightness-110 transition-all disabled:opacity-50 whitespace-nowrap"
          style={{ boxShadow: "0 6px 20px hsla(54, 93%, 49%, 0.45)" }}
        >
          {isSubmitting ? "Saving..." : "Get Early Access →"}
        </button>
      </div>
      {errorMessage && <p className="mt-3 text-red-300 text-center text-sm font-medium">{errorMessage}</p>}
      <p className="mt-3 text-white/50 text-xs text-center">
        No spam — just dates, lineup, and first dibs on tickets.
      </p>
    </form>
  );
};

const Tickets = () => {
  usePageSEO({
    title: "2027 Early Access — Yacht Rock Detroit",
    description: "Thanks for sailing with us, Detroit. Sign up to get 2027 early access — dates, lineup announcements, and tickets before the public. Yacht Rock Detroit at The Roostertail.",
    keywords: "yacht rock detroit 2027, yacht rock detroit tickets, detroit summer concerts 2027, roostertail concerts, detroit waterfront festival, yacht rock festival detroit",
    ogImage: PHOTOS.CROWD.sunsetStageView,
    breadcrumbs: [
      { name: "Home", url: "https://yachtrockdetroit.com/" },
      { name: "Tickets", url: "https://yachtrockdetroit.com/tickets" },
    ],
  });

  const headerRef = useRef<HTMLElement>(null);

  // Tickets is the #1 page we WANT indexed — explicitly allow it (overrides any
  // stale robots meta) so it ranks while the 2027 list builds.
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "robots";
      document.head.appendChild(meta);
    }
    meta.content = "index, follow, max-image-preview:large";
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  const focusHeroForm = () => {
    document.getElementById("early-access-hero")?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => document.getElementById("early-access-hero")?.focus({ preventScroll: true }), 500);
  };

  return (
    <div className="min-h-screen bg-yacht-cream pb-20 md:pb-0">
      <GlobalHeader heroRef={headerRef} ticketsCta={{ label: "Get 2027 Early Access", onClick: focusHeroForm }} />

      {/* Sticky mobile bar — CTA stays in the thumb zone, scrolls to the form */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-yacht-cream/95 backdrop-blur-sm border-t border-yacht-charcoal/10">
        <button
          onClick={focusHeroForm}
          className="w-full py-4 rounded-full bg-yacht-yellow text-yacht-charcoal font-bold text-base hover:brightness-110 transition-all"
          style={{ boxShadow: "0 4px 14px hsla(54, 93%, 49%, 0.4)" }}
        >
          Get 2027 Early Access →
        </button>
      </div>

      {/* HERO */}
      <section
        ref={headerRef}
        className="relative min-h-fit md:min-h-[70vh] bg-yacht-blue overflow-hidden flex items-center justify-center"
      >
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <img
          src={boatsGraphic}
          alt=""
          className="absolute pointer-events-none z-0
            w-[800%] left-1/2 -translate-x-1/2 top-0
            sm:w-[150%] sm:-top-[30%]
            lg:w-[90%] lg:-left-[10%] lg:-top-[30%] lg:translate-x-0"
          style={{ opacity: 0.15, filter: "brightness(0) invert(1)" }}
        />

        <div className="relative z-20 w-full flex flex-col items-center justify-center text-center pt-28 pb-14 md:pt-48 md:pb-32 px-6">
          <p className="text-yacht-yellow/80 text-sm md:text-base font-medium tracking-[0.3em] uppercase mb-5">
            Yacht Rock Detroit · The Roostertail · Detroit
          </p>
          <h1 className="text-[13vw] md:text-[7.5vw] font-bold text-white leading-[0.95] tracking-tight">
            Thanks,
            <br />
            Detroit.
          </h1>
          <p
            className="mt-5 text-white/85 text-base md:text-xl italic max-w-xl mx-auto"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          >
            Thanks for sailing with us. Sign up to get 2027 early access — dates, lineup, and tickets before the public.
          </p>
          <div className="mt-8 w-full">
            <EarlyAccessForm inputId="early-access-hero" />
          </div>
        </div>
      </section>

      {/* What early access gets you — editorial index, no cards */}
      <section className="bg-yacht-cream py-16 md:py-24 px-6 md:px-16 border-b border-yacht-charcoal/10">
        <div className="max-w-5xl mx-auto">
          <p className="text-yacht-charcoal/50 text-sm font-medium tracking-[0.3em] uppercase mb-5 md:mb-6">
            2027 Early Access
          </p>
          <h2 className="text-4xl md:text-6xl font-bold text-yacht-charcoal tracking-tight leading-[1.02] mb-4">
            You'll know<br className="hidden md:block" /> before anyone else.
          </h2>
          <p
            className="text-yacht-charcoal/75 text-lg md:text-2xl italic leading-snug mb-10 md:mb-14"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          >
            25+ live acts, five stages, a weekend on the Detroit River — and 2027 is already in the works.
          </p>
          <div className="border-t border-yacht-charcoal/20">
            {perks.map((item) => (
              <div
                key={item.num}
                className="flex items-baseline gap-5 md:gap-8 py-6 md:py-8 border-b border-yacht-charcoal/20"
              >
                <span className="text-yacht-charcoal/35 text-sm md:text-base font-medium tabular-nums">
                  {item.num}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-2xl md:text-4xl font-bold text-yacht-charcoal tracking-tight leading-none">
                    {item.title}
                  </span>
                  <span className="block mt-2 text-yacht-charcoal/60 text-sm md:text-base leading-relaxed">
                    {item.blurb}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sponsors */}
      <section className="bg-yacht-cream pb-16 md:pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          <SponsorsBlock />
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Tickets;
